import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isEmpty, map, filter, uniq, sortBy, union, findIndex } from 'underscore';
import { CommandBarButton } from '@fluentui/react/lib/Button';
import { MessageBar } from '@fluentui/react/lib/MessageBar';
import { useConfirmDialog } from '@bingads-webui-campaign-react/mca-core';

import {
  useDefaultEditorStyle,
  getComponentById,
  useFloatingBannerMessage,
} from '@bingads-webui-campaign-react/smart-component-util';
import {
  DataServiceContext,
  useEditorStatusContext,
  useSiteConfig,
  editorStatusLoadingWrapperForPromise,
  useInstrumentationServiceContext,
  useIsSmartPageCustomizedUpdateEnabled,
} from '@bingads-webui-campaign-react/smart-page-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  facebookPageInfoFilter,
  importFromFacebook,
  hasFBOAuthError,
} from '@bingads-webui-campaign-react/smart-page-facebook-import-dialog';
import { ItemPicker, CardDialog } from '@bingads-webui-campaign-react/smart-component-card';
import { CARD_SOURCE } from '@bingads-webui-campaign-react/smart-component-card/src/constants';

import { SelectUpdateDialog } from './components/select-update-dialog';
import { SmartUpdateCard } from './components/smart-update-card';
import { useDataState } from './components/use-data-state';
import { UPDATE_SOURCE, MaxPostsCount, softLimit, hardLimit } from './components/constants';

const editableConditionCheck = item => item.source === CARD_SOURCE.SMARTPAGE;

const getStyles = (_, { palette = {} }) => ({
  updatesEditorContainer: {
  },
  updatesListLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 16,
    margin: '0px 38px 16px 20px',
  },
  addBtn: {
    marginLeft: 'auto',
    '&:enabled': {
      color: palette.themePrimary,
    },
  },
  noData: {
    background: '#F3F2F0',
    padding: '24px 22px',
  },
  commonIcon: {
    color: '#0078D4',
    fontSize: '16px',
    lineHeight: '16px',
    margin: '0px 4px',
    fontFamily: 'MsAds MDL2 Assets',
    fontStyle: 'normal',
  },
  fbIcon: {
    '&:before': {
      content: '"\\ECB3"',
    },
  },
  editCreateIcon: {
    '&:before': {
      content: '"\\F3C9"',
    },
  },
  confirmDeleteOldestPostDialog: {
    '& .ms-Dialog-main': {
      minWidth: 600,
    },
    '& .ms-Dialog-action': {
      float: 'none',
    },
  },
});

export const UnStyledSmartUpdateEditor = ({
  i18n,
  componentId,
  classes,
  gotoSettings,
}) => {
  const {
    titleClassName,
    descriptionClassName,
    sectionTitleClassName,
    infoMessageBarClassName,
  } = useDefaultEditorStyle();

  const [, dispatchEditorStatus] = useEditorStatusContext();
  const { siteConfig, dispatchSiteConfig } = useSiteConfig();
  const { smartPageService, hostSystemScenario } = useContext(DataServiceContext);
  const [startFetchFbUpdates, setStartFetchFbUpdates] = useState(false);
  const [isFbSupported, setIsFbSupported] = useState(false);
  const [isFbInitializing, setIsFbInitializing] = useState(true);
  const [allFbUpdates, setAllFbUpdates] = useState([]);
  const [targetUpdateIndex, setTargetUpdateIndex] = useState(-1);
  const [showFbImportDialog, setShowFbImportDialog] = useState(false);
  const [showCreateUpdateDialog, setShowCreateUpdateDialog] = useState(false);
  const [pendingDeleteUpdateId, setPendingDeleteUpdateId] = useState('');
  const [currentPostType, setCurrentPostType] = useState(UPDATE_SOURCE.FACEBOOK);
  const componentConfig = getComponentById(siteConfig, componentId) || {};
  const [selectedUpdates, setSelectedUpdates] = useState(componentConfig.updates || []);
  const { noDataMsgKey, noData } = useDataState(allFbUpdates, selectedUpdates);
  const { showWarning } = useFloatingBannerMessage(dispatchEditorStatus);
  const logger = useInstrumentationServiceContext();
  const isSmartPageCustomizedUpdateEnabled = useIsSmartPageCustomizedUpdateEnabled();

  const onSaveChange = useCallback(({ newSelectedUpdates, imagesToAdd, imageIdsToRemove }) => {
    dispatchSiteConfig({
      type: 'UPDATE_COMPONENT_BY_ID',
      payload: {
        ...componentConfig,
        updates: newSelectedUpdates,
      },
    });

    const updateImagesPayload = {};

    if (!isEmpty(imagesToAdd)) {
      updateImagesPayload.imagesToAdd = imagesToAdd;
    }

    if (!isEmpty(imageIdsToRemove)) {
      updateImagesPayload.imageIdsToRemove = imageIdsToRemove;
    }

    if (!isEmpty(updateImagesPayload)) {
      dispatchSiteConfig({
        type: 'UPDATE_IMAGES',
        payload: updateImagesPayload,
      });
    }

    setSelectedUpdates(newSelectedUpdates);
    setShowFbImportDialog(false);
    setShowCreateUpdateDialog(false);
    setTargetUpdateIndex(-1);
  }, [componentConfig, dispatchSiteConfig]);

  const onEdit = useCallback((index) => {
    setTargetUpdateIndex(index);
    setShowCreateUpdateDialog(true);
  }, []);

  const onDelete = useCallback((index) => {
    if (index >= selectedUpdates.length) {
      return;
    }
    setSelectedUpdates((selectedUpdateState) => {
      const [deletedUpdate] = selectedUpdateState.splice(index, 1);

      if (deletedUpdate && deletedUpdate.imageId) {
        dispatchSiteConfig({
          type: 'UPDATE_IMAGES',
          payload: {
            imageIdsToRemove: [deletedUpdate.imageId],
          },
        });
      }

      dispatchSiteConfig({
        type: 'UPDATE_COMPONENT_BY_ID',
        payload: {
          ...componentConfig,
          updates: [...selectedUpdateState],
        },
      });

      const newState = [...selectedUpdateState];

      return newState;
    });
  }, [selectedUpdates, componentConfig, dispatchSiteConfig]);

  const onSaveUpdateChange =
    useCallback(({ newSelectedUpdates, imagesToAdd, imageIdsToRemove }) => {
      const allSelectedUpdates = sortBy(
        uniq(union(newSelectedUpdates, selectedUpdates || []), update => update.id),
        update => new Date(update.createdTime).getTime() || 0
      ).reverse();

      const deleteUpdateIndex =
        findIndex(allSelectedUpdates, update => update.id === pendingDeleteUpdateId);

      if (deleteUpdateIndex !== -1) {
        allSelectedUpdates.splice(deleteUpdateIndex, 1);
        setPendingDeleteUpdateId('');
      }

      onSaveChange({ newSelectedUpdates: allSelectedUpdates, imagesToAdd, imageIdsToRemove });
    }, [onSaveChange, selectedUpdates, pendingDeleteUpdateId]);

  const onConfirmDeleteOldestPost = useCallback(() => {
    const deleteIndex = selectedUpdates.length - 1;

    if (deleteIndex > -1) {
      setPendingDeleteUpdateId(selectedUpdates[deleteIndex].id);
    }

    if (currentPostType === UPDATE_SOURCE.FACEBOOK) {
      setStartFetchFbUpdates(true);
      setShowFbImportDialog(true);
    } else {
      setShowCreateUpdateDialog(true);
    }
  }, [selectedUpdates, currentPostType]);

  const [
    confirmDeleteOldestPostDialog,
    showConfirmDeleteOldestPostDialog,
    isConfirmDeleteOldestPostDialogVisible,
  ] = useConfirmDialog({
    title: i18n.getString(_TL_('You\'ve exceeded the maximum number of posts {{max}}.'), { max: MaxPostsCount }),
    content: i18n.getString(_TL_('Before you can create a new post, you\'ll need to delete an old one. What would you like to do?')),
    confirmText: i18n.getString(_TL_('Delete oldest post')),
    onConfirm: onConfirmDeleteOldestPost,
    cancelText: i18n.getString(_TL_('Cancel and choose which post to delete')),
    className: classnames(classes.confirmDeleteOldestPostDialog, 'smart-page-delete-oldest-post-confirm-dialog'),
    modalProps: { isBlocking: true },
  });

  const openFbImportDialog = useCallback(() => {
    if (selectedUpdates.length < MaxPostsCount) {
      setStartFetchFbUpdates(true);
      setShowFbImportDialog(true);
    } else {
      setCurrentPostType(UPDATE_SOURCE.FACEBOOK);
      showConfirmDeleteOldestPostDialog();
    }
  }, [showConfirmDeleteOldestPostDialog, selectedUpdates]);

  const closeFbImportDialog = useCallback(() => {
    setPendingDeleteUpdateId('');
    setShowFbImportDialog(false);
  }, []);

  const openCreateUpdateDialog = useCallback(() => {
    if (selectedUpdates.length < MaxPostsCount) {
      setShowCreateUpdateDialog(true);
    } else {
      setCurrentPostType(UPDATE_SOURCE.SMARTPAGE);
      showConfirmDeleteOldestPostDialog();
    }
  }, [showConfirmDeleteOldestPostDialog, selectedUpdates]);

  const closeCreateUpdateDialog = useCallback(() => {
    setShowCreateUpdateDialog(false);
    setTargetUpdateIndex(-1);
    setPendingDeleteUpdateId('');
  }, []);

  useEffect(() => {
    if (!isSmartPageCustomizedUpdateEnabled || startFetchFbUpdates) {
      importFromFacebook(
        siteConfig,
        smartPageService,
        facebookPageInfoFilter.posts,
        hostSystemScenario
      )
        .finally(editorStatusLoadingWrapperForPromise(dispatchEditorStatus))
        .then(({ posts }) => {
          setAllFbUpdates(map(posts, post => ({
            message: post.Message,
            photo: post.FullPicture,
            id: post.Id,
            createdTime: post.CreatedTime,
          })));
          setIsFbSupported(true);
        })
        .catch((err) => {
          if (err) {
            if (hasFBOAuthError(err)) {
              logger.logTrace('Encounter Facebook OAuth error while importing FB posts');
              showWarning(i18n.getString(_TL_('We could not access your Facebook profile, please reconnect it in Settings.')));
              setIsFbSupported(false);
            } else if (err.message !== 'NO_FB_AUTH') {
              setIsFbSupported(true);
              logger.logError(`Import posts from facebook failed. error: ${err}, error string: ${JSON.stringify(err)}`);
              showWarning(i18n.getString(_TL_('We were not able to find posts from this Facebook page. Please try again or reimport your Facebook Page.')));
            }
          }
        })
        .finally(() => setIsFbInitializing(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startFetchFbUpdates, isSmartPageCustomizedUpdateEnabled]);

  const menuProps = useMemo(() => (isSmartPageCustomizedUpdateEnabled ?
    ({
      items: [
        {
          key: 'fbImport',
          text: i18n.getString(_TL_('Import from Facebook')),
          iconProps: {
            className: classnames(classes.commonIcon, classes.fbIcon),
          },
          'data-automation-id': 'fb-import-option', // optional
          onClick: openFbImportDialog,
        },
        {
          key: 'createMyOwn',
          text: i18n.getString(_TL_('Create my own')),
          iconProps: {
            className: classnames(classes.commonIcon, classes.editCreateIcon),
          },
          'data-automation-id': 'create-my-own-option', // optional
          onClick: openCreateUpdateDialog,
        },
      ],
    }) : undefined), [
    i18n,
    isSmartPageCustomizedUpdateEnabled,
    openCreateUpdateDialog,
    openFbImportDialog,
    classes,
  ]);

  const updateEditorTexts = useMemo(() => {
    if (isSmartPageCustomizedUpdateEnabled) {
      return {
        titleText: i18n.getString(_TL_('Social posts')),
        descriptionText: i18n.getString(_TL_('Tell customers what\'s going on with your business. Create new posts or import them directly from your Facebook Business Page to your Smart Page. You may publish a maximum of {{max}} updates on your Smart Page.'), { max: MaxPostsCount }),
        addBtnText: i18n.getString(_TL_('Add posts')),
        updateListText: i18n.getString(_TL_('Your posts')),
      };
    }

    return {
      titleText: i18n.getString(_TL_('Social posts')),
      descriptionText: i18n.getString(_TL_('Select up to {{max}} posts from your Facebook page.'), { max: MaxPostsCount }),
      addBtnText: i18n.getString(_TL_('Add posts')),
      updateListText: i18n.getString(_TL_('Posts from the last 90 days')),
    };
  }, [isSmartPageCustomizedUpdateEnabled, i18n]);

  return (
    <div className={classes.updatesEditorContainer}>
      <h2 className={titleClassName}>
        {updateEditorTexts.titleText}
      </h2>
      <div className={descriptionClassName}>
        {updateEditorTexts.descriptionText}
      </div>
      <div className="updates-list">
        <div className={classes.updatesListLabel}>
          <span className={sectionTitleClassName}>{updateEditorTexts.updateListText}</span>
          <CommandBarButton
            iconProps={{ iconName: 'Add' }}
            text={updateEditorTexts.addBtnText}
            onClick={openFbImportDialog}
            className={classes.addBtn}
            disabled={isSmartPageCustomizedUpdateEnabled ?
              false : (noData || (selectedUpdates && selectedUpdates.length === MaxPostsCount))}
            menuProps={menuProps}
          />
        </div>
        {
          noDataMsgKey ?
            <MessageBar className={classnames(infoMessageBarClassName, classes.noData, 'smart-page-update-no-data')} isMultiline>
              {
                isSmartPageCustomizedUpdateEnabled ?
                  i18n.getString(_TL_('You haven\'t imported any Facebook posts. Select "Add posts" to import or create your own post.')) :
                  i18n.getString(noDataMsgKey)
              }
            </MessageBar> :
            <ItemPicker
              items={selectedUpdates}
              i18n={i18n}
              ItemComponent={SmartUpdateCard}
              onEdit={onEdit}
              onDelete={onDelete}
              getDeleteButtonAriaLabel={
                index => i18n.getString(_TL_('Delete post {{number}}'), { number: index })
              }
              getEditButtonAriaLabel={
                index => i18n.getString(_TL_('Edit post {{number}}'), { number: index })
              }
              editable={isSmartPageCustomizedUpdateEnabled}
              editableConditionCheck={editableConditionCheck}
            />
        }
      </div>
      {isConfirmDeleteOldestPostDialogVisible && confirmDeleteOldestPostDialog}
      {
        showFbImportDialog &&
        <SelectUpdateDialog
          i18n={i18n}
          onAdd={onSaveUpdateChange}
          onCancel={closeFbImportDialog}
          isFbSupported={isFbSupported}
          isFbInitializing={isFbInitializing}
          gotoSettings={gotoSettings}
          updates={allFbUpdates}
          // The last update id (pendingDeleteUpdateId) will be filtered out from the select list,
          // and will be removed from the selected list when saving.
          preselectedUpdates={map(
            filter(selectedUpdates || [], update => update.id !== pendingDeleteUpdateId),
            update => update.id
          )}
          pendingDeleteUpdateId={pendingDeleteUpdateId}
        />
      }
      {
        showCreateUpdateDialog && isSmartPageCustomizedUpdateEnabled &&
        <CardDialog
          ItemComponent={SmartUpdateCard}
          characterSoftLimit={softLimit}
          characterHardLimit={hardLimit}
          i18n={i18n}
          onAdd={onSaveUpdateChange}
          onCancel={closeCreateUpdateDialog}
          targetCardIndex={targetUpdateIndex}
          preselectedCards={selectedUpdates || []}
          gotoSettings={gotoSettings}
        />
      }
    </div>
  );
};

UnStyledSmartUpdateEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  componentId: PropTypes.string.isRequired,
  classes: PropTypes.shape({
    updatesListLabel: PropTypes.string.isRequired,
  }).isRequired,
  gotoSettings: PropTypes.func.isRequired,
};

export const SmartUpdateEditor = withDefaultStyles(
  UnStyledSmartUpdateEditor,
  getStyles
);
